import React from "react"
import { useParams } from "react-router-dom"
import { Loader } from "@mantine/core"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CampaignSubpageHeader } from "QuorumGrassroots/widgets/ReusableComponents/CampaignSubpageHeader"
import {
    SubpageContainer,
    NextElectionBannerWrapper,
    Wrapper,
} from "QuorumGrassroots/pages/campaign/key-dates/KeyDatesPage.styles"
import { NextElectionInfo } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/NextElectionInfo/NextElectionInfo"
import { useCampaign } from "QuorumGrassroots/services/grassrootsCampaign"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import { CollapsibleKeyDatesSection } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CollapsibleKeyDatesSection/CollapsibleKeyDatesSection"
import { EARLY_VOTING_DATES, LAST_DAY_TO_REGISTER_DATES, REQUEST_AN_ABSENTEE_BALLOT_DATES } from "./mocks"

export const KeyDatesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()

    const { data: campaign, isLoading, isError } = useCampaign(id)

    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign?.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign?.social_share_text ?? `Help ${campaign?.name} get out the vote!`,
        imageUrl: campaign?.social_image_url,
    })

    // Mocked calendar event data - will be replaced with real data from backend
    const mockCalendarEvent = {
        title: "New York General Election",
        description: "General Election Day - Make your voice heard!",
        start: "2024-11-05T08:00:00",
        end: "2024-11-05T20:00:00",
        timezone: "America/New_York",
    }

    if (isLoading)
        return (
            <Wrapper>
                <SubpageContainer>
                    <Loader size="sm" /> Loading...
                </SubpageContainer>
            </Wrapper>
        )
    if (isError || !campaign)
        return (
            <Wrapper>
                <SubpageContainer>
                    <p>Error loading campaign. Please try again later.</p>
                </SubpageContainer>
            </Wrapper>
        )

    return (
        <SubpageContainer>
            <CampaignTitleSubtitle campaign={campaign} />
            <CampaignSubpageHeader title="Check Key Dates" backUrl={`/campaign/${id}`} dataCy="key-dates-header" />

            {/* Next Election banner with calendar integration */}
            <NextElectionBannerWrapper>
                <NextElectionInfo
                    electionName="New York General Election"
                    electionDate="Nov 5, 2024"
                    calendarEvent={mockCalendarEvent}
                />
            </NextElectionBannerWrapper>

            <CollapsibleKeyDatesSection title="Last day to register" dates={LAST_DAY_TO_REGISTER_DATES} defaultOpen />

            <CollapsibleKeyDatesSection
                title="Request an absentee ballot"
                dates={REQUEST_AN_ABSENTEE_BALLOT_DATES}
                defaultOpen
            />

            <CollapsibleKeyDatesSection title="Early voting" dates={EARLY_VOTING_DATES} />

            <GotvCampaignFooter
                socialPlatforms={socialPlatforms}
                socialHeading={campaign.social_header ?? DEFAULT_SOCIAL_HEADER}
            />
        </SubpageContainer>
    )
}
